import type { DayShape } from "./vesselIdentity.js";

/**
 * Tiny SVG icon of a vessel day shape, drawn matt-black on a
 * translucent halyard line. Sized for the target popover (40×60 by
 * default) — caller can override `size`.
 */
export function DayShapeIcon(props: {
  shape: DayShape;
  size?: number;
}): JSX.Element {
  const w = props.size ?? 40;
  const h = w * 1.5;
  return (
    <svg
      viewBox="0 0 40 60"
      width={w}
      height={h}
      role="img"
      aria-label={`Day shape: ${props.shape}`}
    >
      <line x1="20" y1="0" x2="20" y2="60" stroke="rgba(154,166,184,0.4)" strokeWidth="0.6" />
      {renderShape(props.shape)}
    </svg>
  );
}

function renderShape(shape: DayShape): JSX.Element {
  const black = "#0b0b10";
  switch (shape) {
    case "ball":
      return <circle cx="20" cy="20" r="6" fill={black} />;
    case "two-balls":
      return (
        <>
          <circle cx="20" cy="14" r="5" fill={black} />
          <circle cx="20" cy="32" r="5" fill={black} />
        </>
      );
    case "three-balls":
      return (
        <>
          <circle cx="20" cy="10" r="4.5" fill={black} />
          <circle cx="20" cy="26" r="4.5" fill={black} />
          <circle cx="20" cy="42" r="4.5" fill={black} />
        </>
      );
    case "cylinder":
      // Tall black rectangle with rounded top/bottom = cylindrical
      // shape silhouette as it appears at distance.
      return <rect x="14" y="14" width="12" height="22" rx="2" fill={black} />;
    case "ball-diamond-ball":
      return (
        <>
          <circle cx="20" cy="10" r="5" fill={black} />
          <polygon points="20,21 28,30 20,39 12,30" fill={black} />
          <circle cx="20" cy="50" r="5" fill={black} />
        </>
      );
    case "diamond":
      return <polygon points="20,12 30,28 20,44 10,28" fill={black} />;
    case "two-cones-apex-to-apex":
      // Upper cone point-down, lower cone point-up — meeting at the
      // apex in the middle. Forms the "basket" shape.
      return (
        <>
          <polygon points="10,8 30,8 20,28" fill={black} />
          <polygon points="20,28 30,48 10,48" fill={black} />
        </>
      );
    case "cone-down":
      return <polygon points="10,12 30,12 20,40" fill={black} />;
  }
}
